if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.thumbnailDriven = false;
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // CUSTOM SUB VARIANT IMAGES
        this.initVariantImages();

        // CUSTOM NAV BUTTONS
        this.setupCustomNavButtons();

        // MOBILE SLIDER SYNC
        this.initMobileSliderSync();
      }

      // CUSTOM SUB VARIANT IMAGES
      // ------------------------------------------------------------------
      // Images are tagged with data-variant-colour (e.g. "Black") rather
      // than a specific variant ID.  This means all size variants that
      // share the same colour (Small/Black, Medium/Black, Large/Black)
      // correctly show/hide the same set of images when the user switches
      // sizes without changing colour.
      // ------------------------------------------------------------------

      initVariantImages() {
        // Parse the colour map injected by Liquid: { "variantId": "Colour", ... }
        try {
          this._variantColours = JSON.parse(this.dataset.variantColours || '{}');
        } catch (e) {
          this._variantColours = {};
        }

        const initialVariantId = this.dataset.selectedVariantId;
        if (initialVariantId) {
          this.updateVariantImages(initialVariantId);
        }

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { variant } }) => {
            if (variant?.id) {
              this.updateVariantImages(variant.id);
            }
          });
        }
      }

      /**
       * Returns the colour option value for a given variant ID.
       * Falls back to null if no colour map is available (e.g. products
       * without a Colour option).
       */
      getVariantColour(variantId) {
        return this._variantColours[String(variantId)] ?? null;
      }

      updateVariantImages(selectedVariantId) {
        const selectedColour = this.getVariantColour(selectedVariantId);

        // Always show untagged images — they're shared across all colours
        this.querySelectorAll(
          '[data-media-id]:not([data-variant-colour]), .thumbnail-list__item:not([data-variant-colour])'
        ).forEach((el) => {
          el.classList.remove('hidden');
          el.style.display = '';
        });

        // Show/hide colour-tagged images based on selected colour
        let visibleCount = 0;
        const allVariantImages = this.querySelectorAll(
          '.product__media-item--variant[data-variant-colour], .thumbnail-list_item--variant-custom[data-variant-colour]'
        );

        allVariantImages.forEach((el) => {
          const elColour = el.getAttribute('data-variant-colour');
          const shouldShow = selectedColour === null || elColour === selectedColour;

          if (shouldShow) {
            el.classList.remove('hidden');
            el.style.display = '';
            visibleCount++;
          } else {
            el.classList.add('hidden');
            el.style.display = 'none';
          }
        });

        // If still nothing visible (colour had no matching uploads), show everything
        if (visibleCount === 0) {
          allVariantImages.forEach((el) => {
            el.classList.remove('hidden');
            el.style.display = '';
          });
        }
      }
      // END CUSTOM SUB VARIANT IMAGES

      // MOBILE SLIDER SYNC
      initMobileSliderSync() {
        const slider = this.elements.viewer.querySelector('ul[id^="Slider-Gallery-"]');
        if (!slider) return;

        // Reset flag when user manually starts swiping
        slider.addEventListener('touchstart', () => {
          this.thumbnailDriven = false;
        }, { passive: true });

        const observer = new IntersectionObserver((entries) => {
          if (this.mql.matches) return;
          if (this.thumbnailDriven) return;

          entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            const slide = entry.target;
            const mediaId = slide.dataset.mediaId;
            if (!mediaId) return;

            const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (thumbnail) this.setActiveThumbnail(thumbnail);
          });
        }, {
          root: slider,
          threshold: 0.5
        });

        slider.querySelectorAll(':scope > li').forEach(slide => observer.observe(slide));
      }
      // END MOBILE SLIDER SYNC

      // CUSTOM NAV BUTTONS
      setupCustomNavButtons() {
        const controls = this.querySelectorAll('button[data-drive-viewer]');
        if (!controls.length) return;

        const thumbsList = this.elements.thumbnails?.querySelector('ul[id^="Slider-Thumbnails-"]');
        if (!thumbsList) return;

        controls.forEach((control) => {
          control.addEventListener('click', (e) => {
            e.preventDefault();

            const allThumbs = Array.from(thumbsList.querySelectorAll('button.thumbnail[aria-controls^="GalleryViewer-"]'));
            const visibleThumbs = allThumbs
              .filter(btn => {
                const li = btn.closest('li');
                return li && li.offsetParent !== null;
              })
              .sort((a, b) => {
                const orderA = parseInt(window.getComputedStyle(a.closest('li')).order) || 0;
                const orderB = parseInt(window.getComputedStyle(b.closest('li')).order) || 0;
                return orderA - orderB;
              });

            if (visibleThumbs.length < 2) return;

            let currentIndex = visibleThumbs.findIndex((b) => b.getAttribute('aria-current') === 'true');
            if (currentIndex === -1) currentIndex = 0;

            const dir = control.dataset.driveViewer;
            const delta = dir === 'next' ? 1 : -1;
            const nextIndex = (currentIndex + delta + visibleThumbs.length) % visibleThumbs.length;

            visibleThumbs[nextIndex].click();
          });
        });
      }
      // END CUSTOM NAV BUTTONS

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.thumbnailDriven = true;
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
          setTimeout(() => { this.thumbnailDriven = false; }, 200);
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
